import React from 'react';
import { useInView } from '../hooks/useInView';

const HowItWorks: React.FC = () => {
  const [headingRef, headingInView] = useInView();
  const [stepsRef, stepsInView] = useInView();
  const [benefitsRef, benefitsInView] = useInView();

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const steps = [
    {
      number: "01",
      title: "Choose Your Music",
      description: "You select your music from your own library and submit it to ChoralPlayer.",
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
        </svg>
      )
    },
    {
      number: "02",
      title: "We Break It Down",
      description: "We break down the music into individual parts and create a practice track for each part.",
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      )
    },
    {
      number: "03",
      title: "You Get Final Say",
      description: "We'll let you know when the track is ready and you can make any final adjustments. You're in control.",
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
        </svg>
      )
    },
    {
      number: "04",
      title: "Upload to The Player",
      description: "We upload your track to the ChoralPlayer web-player so you can start practicing.",
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      )
    }
  ];

  const benefits = [
    {
      title: "Professional Quality Tracks",
      description: "Hand-crafted tracks of all vocal parts, with incredible accuracy."
    },
    {
      title: "Your Repertoire",
      description: "Our tracks are custom-made for your choir. You choose the music, we make the tracks."
    },
    {
      title: "Intuitive Design and Simple UI",
      description: "Anyone can use ChoralPlayer with ease. Just select your music and start practicing."
    }
  ];

  return (
    <section id="how-it-works" className="py-20 px-4 bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="container mx-auto">
        <div
          ref={headingRef}
          className={`text-center mb-16 ${headingInView ? 'animate-fade-up' : 'opacity-0'}`}
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            How
            <span className="gradient-text"> ChoralPlayer</span>
            <span className="block">Works</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Get started in minutes with our simple, intuitive process designed
            to make choral practice more effective and enjoyable.
          </p>
        </div>

        <div ref={stepsRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
          {steps.map((step, index) => (
            <div
              key={index}
              className={`relative ${stepsInView ? 'animate-fade-up' : 'opacity-0'}`}
              style={{ animationDelay: stepsInView ? `${index * 100}ms` : undefined }}
            >
              {index < steps.length - 1 && (
                <div
                  className={`hidden lg:block absolute top-16 left-full h-0.5 bg-gradient-to-r from-blue-500 to-blue-700 z-0 ${stepsInView ? 'animate-fade-in' : 'opacity-0'}`}
                  style={{ width: 'calc(100% - 2rem)', animationDelay: stepsInView ? '950ms' : undefined, animationFillMode: 'both' }}
                />
              )}
              <div className="card text-center relative z-10">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-700 rounded-full flex items-center justify-center mx-auto mb-6">
                  <div className="text-white">{step.icon}</div>
                </div>
                <div className="absolute -top-4 -right-4 w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-700 rounded-full flex items-center justify-center">
                  <span className="text-white text-sm font-bold">{step.number}</span>
                </div>
                <h3 className="text-xl font-semibold mb-4">{step.title}</h3>
                <p className="text-gray-600 leading-relaxed">{step.description}</p>
              </div>
            </div>
          ))}
        </div>

        <div ref={benefitsRef} className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className={benefitsInView ? 'animate-fade-up' : 'opacity-0'}>
            <h3 className="text-3xl font-bold mb-6">
              Why Choirs Choose
              <span className="gradient-text"> ChoralPlayer</span>
            </h3>
            <div className="space-y-6">
              {benefits.map((benefit, index) => (
                <div
                  key={index}
                  className={`flex items-start space-x-4 ${benefitsInView ? 'animate-fade-up' : 'opacity-0'}`}
                  style={{ animationDelay: benefitsInView ? `${100 + index * 100}ms` : undefined }}
                >
                  <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-2">{benefit.title}</h4>
                    <p className="text-gray-600">{benefit.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div
            className={`card ${benefitsInView ? 'animate-fade-up' : 'opacity-0'}`}
            style={{ animationDelay: benefitsInView ? '200ms' : undefined }}
          >
            <div className="text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-blue-700 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-10 h-10 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M18 3a1 1 0 00-1.196-.98l-10 2A1 1 0 006 5v9.114A4.369 4.369 0 005 14c-1.657 0-3 .895-3 2s1.343 2 3 2 3-.895 3-2V7.82l8-1.6v5.894A4.369 4.369 0 0015 12c-1.657 0-3 .895-3 2s1.343 2 3 2 3-.895 3-2V3z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold mb-4">Ready to Get Started?</h3>
              <p className="text-gray-600 mb-6">
                Start using ChoralPlayer today to improve your choir's performance.
              </p>
              <button onClick={() => scrollToSection('contact')} className="btn btn-primary w-full">Contact Us Now</button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
